(() => {
  'use strict';

  const root = document.querySelector(
    '.template-product .product-details-page'
  );

  const dataNode = document.getElementById(
    'ZyviraProductInventory'
  );

  if (!root || !dataNode) return;

  const number = (value, fallback = 0) => {
    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  };

  const positiveInt = (value, fallback = 1) => {
    const parsed = Math.floor(
      number(value, fallback)
    );

    return parsed > 0
      ? parsed
      : fallback;
  };

  let rawVariants;

  try {
    rawVariants = JSON.parse(
      dataNode.textContent || '[]'
    );
  } catch (error) {
    console.warn(
      '[Zyvira] Invalid product inventory data.',
      error
    );

    return;
  }

  const variants = new Map(
    rawVariants.map((item) => [
      String(item.id),
      {
        id: String(item.id),

        available:
          item.available !== false,

        inventoryManagement:
          String(
            item.inventoryManagement || ''
          ).toLowerCase(),

        inventoryPolicy:
          String(
            item.inventoryPolicy || ''
          ).toLowerCase(),

        inventoryQuantity:
          item.inventoryQuantity == null
            ? null
            : Math.max(
                0,
                number(item.inventoryQuantity)
              ),

        cartQuantity:
          Math.max(
            0,
            number(item.cartQuantity)
          ),

        minimum:
          positiveInt(item.minimum),

        maximum:
          item.maximum == null
            ? null
            : Math.max(
                0,
                number(item.maximum)
              ),

        increment:
          positiveInt(item.increment)
      }
    ])
  );

  const cartCounts = new Map(
    [...variants.values()].map((variant) => [
      variant.id,
      variant.cartQuantity
    ])
  );

  const sticky = document.querySelector(
    '.template-product .sticky-btn-atc'
  );

  const mainForm = () =>
    root.querySelector(
      'form[data-type="add-to-cart-form"]'
    );

  const variantInput = () =>
    mainForm()?.querySelector(
      [
        'input.product-variant-id[name="id"]',
        'input[name="id"]'
      ].join(', ')
    );

  const mainQty = () =>
    root.querySelector(
      'input.qty-box-input[name="quantity"]'
    );

  const stickyQty = () =>
    sticky?.querySelector(
      'input.qty-box-input[name="quantity"]'
    );

  const mainButton = () =>
    root.querySelector(
      'button[id^="ProductSubmitButton-"]'
    );

  const stickyButton = () =>
    sticky?.querySelector(
      '.ajax-cart-btn'
    );

  const messageNode = () =>
    root.querySelector(
      '.zyv-product-quantity-message'
    );

  let lastVariantId = '';
  let rendering = false;
  let addLocked = false;
  let forcedMessage = null;

  const selectedVariantId = () =>
    String(
      variantInput()?.value ||
      variantInput()?.getAttribute('value') ||
      ''
    );

  const ruleForSelectedVariant = () => {
    const id = selectedVariantId();
    const variant = variants.get(id);

    if (!variant) return null;

    const cartQuantity = Math.max(
      0,
      number(cartCounts.get(id))
    );

    const totalLimits = [];

    if (variant.maximum != null) {
      totalLimits.push(
        variant.maximum
      );
    }

    const inventoryRestricted =
      variant.inventoryManagement === 'shopify' &&
      variant.inventoryPolicy !== 'continue' &&
      variant.inventoryQuantity != null;

    if (inventoryRestricted) {
      totalLimits.push(
        variant.inventoryQuantity
      );
    }

    const totalMaximum =
      totalLimits.length
        ? Math.min(...totalLimits)
        : Infinity;

    const remaining =
      Number.isFinite(totalMaximum)
        ? Math.max(
            0,
            totalMaximum - cartQuantity
          )
        : Infinity;

    const maximumSelectable =
      Number.isFinite(remaining)
        ? (
            remaining < variant.minimum
              ? 0
              : (
                  variant.minimum +
                  Math.floor(
                    (
                      remaining -
                      variant.minimum
                    ) /
                    variant.increment
                  ) *
                  variant.increment
                )
          )
        : Infinity;

    return {
      ...variant,

      cartQuantity,
      totalMaximum,
      remaining,
      maximumSelectable,

      maximumAlreadyInCart:
        cartQuantity > 0 &&
        Number.isFinite(totalMaximum) &&
        cartQuantity >= totalMaximum,

      blocked:
        !variant.available ||
        (
          Number.isFinite(maximumSelectable) &&
          maximumSelectable < variant.minimum
        )
    };
  };

  const clamp = (
    requested,
    rule
  ) => {
    if (!rule || rule.blocked) {
      return rule?.minimum || 1;
    }

    let value = Math.floor(
      number(
        requested,
        rule.minimum
      )
    );

    value = Math.max(
      rule.minimum,
      value
    );

    value =
      rule.minimum +
      Math.floor(
        (
          value -
          rule.minimum
        ) /
        rule.increment
      ) *
      rule.increment;

    if (
      Number.isFinite(
        rule.maximumSelectable
      )
    ) {
      value = Math.min(
        value,
        rule.maximumSelectable
      );
    }

    return Math.max(
      rule.minimum,
      value
    );
  };

  const setMessage = (
    text,
    state = 'info'
  ) => {
    const node = messageNode();

    if (!node) return;

    if (!text) {
      node.hidden = true;
      node.textContent = '';
      node.removeAttribute(
        'data-state'
      );

      return;
    }

    node.hidden = false;
    node.textContent = text;
    node.dataset.state = state;

    node.setAttribute(
      'role',
      state === 'error' ||
      state === 'blocked'
        ? 'alert'
        : 'status'
    );
  };

  const normalMessage = (rule) => {
    if (!rule) {
      return {
        text: '',
        state: 'info'
      };
    }

    /*
    * Do not reveal available quantity
    * during normal product browsing.
    *
    * Quantity-limit messages are shown
    * only when the customer actually
    * tries to exceed the available stock.
    */

    if (
      rule.maximumAlreadyInCart
    ) {
      return {
        text:
          'You already added the maximum available quantity of this item to your cart.',
        state: 'blocked'
      };
    }

    if (rule.blocked) {
      return {
        text:
          'This item is currently sold out.',
        state: 'blocked'
      };
    }

    return {
      text: '',
      state: 'info'
    };
  };

  const showLimitError = (rule) => {
    forcedMessage =
      rule?.maximumAlreadyInCart
        ? {
            text:
              'You already added the maximum available quantity of this item to your cart.',
            state: 'blocked'
          }
        : {
            text:
              Number.isFinite(
                rule?.totalMaximum
              )
                ? `You can only add ${rule.totalMaximum} of this item to your cart.`
                : 'The selected quantity is not available.',

            state: 'error'
          };
  };

  const updateInput = (
    input,
    value,
    rule
  ) => {
    if (!input || !rule) return;

    input.min = String(
      rule.minimum
    );

    input.step = String(
      rule.increment
    );

    input.dataset.min = String(
      rule.minimum
    );

    input.dataset.cartQuantity = String(
      rule.cartQuantity
    );

    input.dataset.variantId =
      rule.id;

    if (
      Number.isFinite(
        rule.maximumSelectable
      )
    ) {
      const maximum = Math.max(
        0,
        rule.maximumSelectable
      );

      input.max = String(
        maximum
      );

      input.dataset.max = String(
        maximum
      );
    } else {
      input.removeAttribute(
        'max'
      );

      delete input.dataset.max;
    }

    input.value = String(
      value
    );

    input.setAttribute(
      'value',
      String(value)
    );

    input.disabled =
      rule.blocked;

    input.setAttribute(
      'aria-disabled',
      String(rule.blocked)
    );

    const box = input.closest(
      'qty-box, .qty-box'
    );

    const minus =
      box?.querySelector(
        '.qty-bt[name="minus"]'
      );

    const plus =
      box?.querySelector(
        '.qty-bt[name="plus"]'
      );

    if (minus) {
      minus.disabled =
        rule.blocked ||
        value <= rule.minimum;

      minus.setAttribute(
        'aria-disabled',
        String(minus.disabled)
      );
    }

    if (plus) {
      /*
      * Keep + clickable while the item
      * itself is available.
      *
      * If the customer tries to exceed
      * available inventory, validate()
      * will stop the increase and show
      * the quantity warning.
      */
      plus.disabled = rule.blocked;

      plus.setAttribute(
        'aria-disabled',
        String(plus.disabled)
      );
    }
  };

  const setButtonDisabled = (
    button,
    disabled
  ) => {
    if (!button) return;

    button.disabled =
      disabled;

    button.setAttribute(
      'aria-disabled',
      String(disabled)
    );
  };

  const render = (
    preferredQuantity = null
  ) => {
    if (rendering) return;

    const rule =
      ruleForSelectedVariant();

    if (!rule) return;

    rendering = true;

    try {
      const value = clamp(
        preferredQuantity ??
        mainQty()?.value,
        rule
      );

      updateInput(
        mainQty(),
        value,
        rule
      );

      updateInput(
        stickyQty(),
        value,
        rule
      );

      const stickyHiddenQty =
        sticky?.querySelector(
          'input[type="hidden"][name="quantity"]'
        );

      if (stickyHiddenQty) {
        stickyHiddenQty.value =
          String(value);
      }

      setButtonDisabled(
        mainButton(),
        rule.blocked
      );

      root
        .querySelectorAll(
          [
            '.shopify-payment-button button',
            '.shopify-payment-button__button'
          ].join(', ')
        )
        .forEach((button) => {
          setButtonDisabled(
            button,
            rule.blocked
          );
        });

      const currentStickyButton =
        stickyButton();

      if (currentStickyButton) {
        currentStickyButton
          .classList
          .toggle(
            'is-inventory-blocked',
            rule.blocked
          );

        currentStickyButton
          .setAttribute(
            'aria-disabled',
            String(rule.blocked)
          );
      }

      const buttonText =
        rule.maximumAlreadyInCart
          ? 'Already in cart'
          : (
              rule.blocked
                ? 'Sold out'
                : 'Add to cart'
            );

      [
        mainButton(),
        stickyButton()
      ].forEach((button) => {
        const title =
          button?.querySelector(
            '.cart-title'
          );

        if (title) {
          title.textContent =
            buttonText;
        }

        if (button) {
          button.setAttribute(
            'aria-label',
            buttonText
          );
        }
      });

      const message =
        forcedMessage ||
        normalMessage(rule);

      setMessage(
        message.text,
        message.state
      );

      forcedMessage = null;
    } finally {
      rendering = false;
    }
  };

  const refreshCart = async () => {
    try {
      const shopRoot =
        window.Shopify?.routes?.root ||
        '/';

      const response = await fetch(
        `${shopRoot}cart.js?t=${Date.now()}`,
        {
          credentials:
            'same-origin',

          headers: {
            Accept:
              'application/json'
          }
        }
      );

      if (!response.ok) return;

      const cart =
        await response.json();

      cartCounts.clear();

      (
        cart.items || []
      ).forEach((item) => {
        const id = String(
          item.variant_id ||
          item.id ||
          ''
        );

        if (!id) return;

        cartCounts.set(
          id,
          (
            cartCounts.get(id) ||
            0
          ) +
          Math.max(
            0,
            number(item.quantity)
          )
        );
      });
    } catch (error) {
      console.warn(
        '[Zyvira] Cart quantity refresh failed.',
        error
      );
    } finally {
      render();
    }
  };

    /*
   * Keep product-page limits synchronized
   * with cart-drawer quantity changes.
   */
  let stopCartUpdateSync = null;

  if (
    typeof user === 'function' &&
    typeof SP_OBJECT !== 'undefined' &&
    SP_OBJECT.ajxcartRefresh
  ) {
    stopCartUpdateSync = user(
      SP_OBJECT.ajxcartRefresh,
      () => {
        /*
         * First refresh handles immediate updates.
         * Second refresh confirms the final Shopify cart.
         */
        window.setTimeout(
          refreshCart,
          0
        );

        window.setTimeout(
          refreshCart,
          450
        );
      }
    );
  }

  window.addEventListener(
    'pagehide',
    () => {
      if (
        typeof stopCartUpdateSync ===
        'function'
      ) {
        stopCartUpdateSync();
      }
    },
    {
      once: true
    }
  );

  const validate = (
    requested,
    rule
  ) => {
    if (
      !rule ||
      rule.blocked
    ) {
      showLimitError(rule);
      render();

      return false;
    }

    if (
      Number.isFinite(
        rule.maximumSelectable
      ) &&
      requested >
        rule.maximumSelectable
    ) {
      showLimitError(rule);

      render(
        rule.maximumSelectable
      );

      return false;
    }

    return true;
  };

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element
          ? event.target
          : null;

      if (!target) return;

      const quantityButton =
        target.closest(
          [
            '.qty-bt[name="plus"]',
            '.qty-bt[name="minus"]'
          ].join(', ')
        );

      if (
        quantityButton &&
        (
          root.contains(
            quantityButton
          ) ||
          sticky?.contains(
            quantityButton
          )
        )
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const rule =
          ruleForSelectedVariant();

        if (!rule) return;

        const input =
          quantityButton
            .closest(
              'qty-box, .qty-box'
            )
            ?.querySelector(
              'input.qty-box-input'
            );

        const direction =
          quantityButton.name ===
          'plus'
            ? 1
            : -1;

        const requested =
          number(
            input?.value,
            rule.minimum
          ) +
          direction *
          rule.increment;

        if (
          direction > 0 &&
          !validate(
            requested,
            rule
          )
        ) {
          return;
        }

        forcedMessage = null;

        render(requested);

        return;
      }

      const stickyAdd =
        target.closest(
          '.template-product .sticky-btn-atc .ajax-cart-btn'
        );

      if (stickyAdd) {
        /*
         * Stops the theme's old sticky
         * script, which currently
         * submits twice.
         */
        event.preventDefault();
        event.stopImmediatePropagation();

        const rule =
          ruleForSelectedVariant();

        const requested =
          number(
            stickyQty()?.value,
            rule?.minimum || 1
          );

        if (
          !validate(
            requested,
            rule
          )
        ) {
          return;
        }

        render(requested);

        mainButton()?.click();

        return;
      }

      const addButton =
        target.closest(
          'button[id^="ProductSubmitButton-"]'
        );

      if (
        addButton &&
        root.contains(addButton)
      ) {
        const rule =
          ruleForSelectedVariant();

        const requested =
          number(
            mainQty()?.value,
            rule?.minimum || 1
          );

        if (
          !validate(
            requested,
            rule
          ) ||
          addLocked
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();

          return;
        }

        addLocked = true;

        window.setTimeout(
          () => {
            addLocked = false;
          },
          900
        );

        window.setTimeout(
          refreshCart,
          800
        );

        window.setTimeout(
          refreshCart,
          1900
        );

        return;
      }

      const buyNow =
        target.closest(
          [
            '.shopify-payment-button button',
            '.shopify-payment-button__button'
          ].join(', ')
        );

      if (
        buyNow &&
        root.contains(buyNow)
      ) {
        const rule =
          ruleForSelectedVariant();

        const requested =
          number(
            mainQty()?.value,
            rule?.minimum || 1
          );

        if (
          !validate(
            requested,
            rule
          )
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form =
        event.target;

      if (
        !(
          form instanceof
          HTMLFormElement
        ) ||
        form !== mainForm()
      ) {
        return;
      }

      const rule =
        ruleForSelectedVariant();

      const requested =
        number(
          mainQty()?.value,
          rule?.minimum || 1
        );

      if (
        !validate(
          requested,
          rule
        )
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        return;
      }

      if (!addLocked) {
        addLocked = true;

        window.setTimeout(
          () => {
            addLocked = false;
          },
          900
        );

        window.setTimeout(
          refreshCart,
          800
        );

        window.setTimeout(
          refreshCart,
          1900
        );
      }
    },
    true
  );

  document.addEventListener(
    'input',
    (event) => {
      const input =
        event.target;

      if (
        !(
          input instanceof
          HTMLInputElement
        ) ||
        !input.matches(
          'input.qty-box-input[name="quantity"]'
        )
      ) {
        return;
      }

      if (
        !root.contains(input) &&
        !sticky?.contains(input)
      ) {
        return;
      }

      if (input.value === '') {
        return;
      }

      const rule =
        ruleForSelectedVariant();

      if (!rule) return;

      const requested =
        Math.floor(
          number(
            input.value,
            rule.minimum
          )
        );

      if (
        !validate(
          requested,
          rule
        )
      ) {
        return;
      }

      forcedMessage = null;

      render(requested);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const target =
        event.target;

      if (
        target instanceof
          HTMLInputElement &&
        target.matches(
          'input.qty-box-input[name="quantity"]'
        )
      ) {
        if (
          !root.contains(target) &&
          !sticky?.contains(target)
        ) {
          return;
        }

        const rule =
          ruleForSelectedVariant();

        const requested =
          Math.floor(
            number(
              target.value,
              rule?.minimum || 1
            )
          );

        validate(
          requested,
          rule
        );

        render(requested);

        return;
      }

      if (
        target instanceof Element &&
        root.contains(target)
      ) {
        window.requestAnimationFrame(
          () => {
            const id =
              selectedVariantId();

            if (
              id &&
              id !== lastVariantId
            ) {
              lastVariantId = id;
              forcedMessage = null;

              refreshCart();
            } else {
              render();
            }
          }
        );
      }
    },
    true
  );

  /*
   * Some themes update the hidden
   * variant input without firing
   * a normal change event.
   */
  window.setInterval(
    () => {
      const id =
        selectedVariantId();

      if (
        !id ||
        id === lastVariantId
      ) {
        return;
      }

      lastVariantId = id;
      forcedMessage = null;

      refreshCart();
    },
    400
  );

  let initialised = false;

  window.addEventListener(
    'pageshow',
    () => {
      if (initialised) {
        refreshCart();
      }
    }
  );

  lastVariantId =
    selectedVariantId();

  refreshCart().finally(
    () => {
      initialised = true;
    }
  );
})();